/**
 * OrcamentoDetailPage Component
 * 
 * Página de visualização detalhada de um orçamento.
 * Permite visualizar todas as informações, aprovar, cancelar e gerar PDF.
 * 
 * Funcionalidades:
 * - Visualização completa de informações do orçamento
 * - Lista de itens com detalhes
 * - Ações: aprovar, cancelar, abrir (enviar ao cliente)
 * - Geração e download de PDF
 * - Botão para reserva de estoque (se aplicável)
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, Edit3, FileText, CheckCircle2, 
  XCircle, Package, Download, Clock, AlertCircle,
  Calendar,
  ShieldCheck, Printer, Cpu, ShoppingCart, RotateCcw, Copy
} from 'lucide-react'
import { usePermissions, MODULES, ACTIONS } from '../../../hooks/usePermissions'
import { useToast } from '../../../hooks/useToast'
import { quoteService, customerService } from '../../../services/erpApi'
import ConfirmModal from '../../../components/ERP/ConfirmModal/ConfirmModal'
import QuotePDFTemplate from '../../../components/ERP/Orcamentos/QuotePDFTemplate'
import { Toast } from '../../../components'
import Avatar from '../../../components/Avatar/Avatar'
import { exportQuoteElementToPdf } from '../../../utils/exportQuotePdf'
import './OrcamentoDetailPage.css'

function OrcamentoDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()
  const { toast, showToast, hideToast } = useToast()
  const [quote, setQuote] = useState(null)
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [generatingPDF, setGeneratingPDF] = useState(false)
  const [pdfQuoteData, setPdfQuoteData] = useState(null)
  const [engineLogs, setEngineLogs] = useState('Iniciando Motor...')
  const [customer, setCustomer] = useState(null)
  const pdfItemsRef = useRef(null)
  const pdfSummaryRef = useRef(null)
  const [confirmModalState, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null
  })

  const loadOrcamento = useCallback(async () => {
    setLoading(true)
    try {
      const response = await quoteService.getById(id)
      if (response.success && response.data) {
        const quoteData = response.data
        setQuote(quoteData)
        
        // Buscar itens separadamente se não vieram na resposta
        if (quoteData.items && quoteData.items.length > 0) {
          setItems(quoteData.items)
        } else {
          // Tentar buscar itens separadamente
          const itemsResponse = await quoteService.getById(id)
          if (itemsResponse.success && itemsResponse.data?.items) {
            setItems(itemsResponse.data.items)
          }
        }

        // Carregar dados do cliente se houver customer_id
        if (quoteData.customer_id) {
          try {
            const customerResponse = await customerService.getById(quoteData.customer_id)
            if (customerResponse.success && customerResponse.data) {
              setCustomer(customerResponse.data)
            }
          } catch (error) {
            console.error('Erro ao carregar cliente:', error)
          }
        }
      }
    } catch (error) {
      console.error('Erro ao carregar orçamento:', error)
      showToast('Erro ao carregar orçamento', 'error')
    } finally {
      setLoading(false)
    }
  }, [id, showToast])

  useEffect(() => {
    loadOrcamento()
  }, [loadOrcamento])

  const handleApprove = async () => {
    setConfirmModal({
      isOpen: true,
      title: 'Aprovar Orçamento',
      message: `Deseja aprovar o orçamento #${quote.number}?`,
      onConfirm: async () => {
        try {
          const response = await quoteService.approve(id)
          if (response.success) {
            showToast('Orçamento aprovado com sucesso!', 'success')
            loadOrcamento()
          } else {
            showToast(response.message || 'Erro ao aprovar orçamento', 'error')
          }
        } catch (error) {
          console.error('Erro ao aprovar orçamento:', error)
          showToast(error.response?.data?.message || 'Erro ao aprovar orçamento', 'error')
        } finally {
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })
        }
      }
    })
  }

  const handleRevertToDraft = async () => {
    setConfirmModal({
      isOpen: true,
      title: 'Voltar para Rascunho',
      message: `Deseja reverter o orçamento #${quote.number} para rascunho? Ele sairá da lista de aprovados e poderá ser editado novamente.`,
      onConfirm: async () => {
        try {
          const response = await quoteService.revertToDraft(id)
          if (response.success) {
            showToast('Orçamento voltou para rascunho!', 'success')
            navigate(`/erp/orcamentos/${id}/editar`)
          } else {
            showToast(response.message || 'Erro ao reverter orçamento', 'error')
          }
        } catch (error) {
          console.error('Erro ao reverter orçamento:', error)
          showToast(error.response?.data?.message || 'Erro ao reverter orçamento', 'error')
        } finally {
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })
        }
      }
    })
  }

  const handleGeneratePDF = async () => {
    try {
      setGeneratingPDF(true)
      setEngineLogs('Carregando dados do orçamento...')
      
      // Preparar dados do orçamento para o PDF
      const quoteData = {
        id: quote.id,
        number: quote.number || quote.id,
        customer: customer || {
          name: quote.customer_name || 'Não informado',
          cpf_cnpj: quote.customer_cpf_cnpj || customer?.cpf_cnpj || null,
          email: customer?.email || null,
          phone: customer?.phone || null,
          cellphone: customer?.cellphone || null
        },
        validityDate: quote.validity_date,
        paymentCondition: quote.payment_condition,
        customerObservation: quote.customer_observation,
        quoteDate: quote.quote_date || quote.created_at,
        items: items.map(item => ({
          product_name: item.product_name || 'Produto não informado',
          sku: item.sku || '-',
          quantity: item.quantity,
          unit_price: item.unit_price,
          discount_percent: item.discount_percent || 0,
          total_price: item.total_price
        })),
        subtotal: parseFloat(quote.total_amount || 0),
        discount: parseFloat(quote.discount_amount || 0),
        total: parseFloat(quote.final_amount || quote.total_amount || 0),
        status: quote.status
      }

      setPdfQuoteData(quoteData)
      setEngineLogs('Preparando documento...')
      
      // Aguardar um pouco para garantir que os componentes foram renderizados
      await new Promise(r => setTimeout(r, 500))
      
      if (!pdfItemsRef.current) {
        showToast('Erro ao gerar PDF. Tente novamente.', 'error')
        setGeneratingPDF(false)
        return
      }

      setEngineLogs('Renderizando documento...')
      await new Promise(r => setTimeout(r, 200))
      setEngineLogs('Gerando PDF...')

      const fileName = `Orcamento_${quoteData.number || quoteData.id}_${new Date().getFullYear()}_${Date.now()}.pdf`
      await exportQuoteElementToPdf(pdfItemsRef.current, fileName)
      
      setEngineLogs('Concluído!')
      await new Promise(r => setTimeout(r, 200))
      
      showToast('PDF gerado com sucesso!', 'success')
    } catch (error) {
      console.error('Erro ao gerar PDF:', error)
      showToast('Erro ao gerar PDF. Tente novamente.', 'error')
    } finally {
      setGeneratingPDF(false)
      setPdfQuoteData(null)
    }
  }


  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: 'Rascunho', className: 'quote-detail-status-draft', icon: FileText },
      open: { label: 'Aberto', className: 'quote-detail-status-open', icon: Clock },
      approved: { label: 'Aprovado', className: 'quote-detail-status-approved', icon: CheckCircle2 },
      expired: { label: 'Vencido', className: 'quote-detail-status-expired', icon: AlertCircle },
      cancelled: { label: 'Cancelado', className: 'quote-detail-status-cancelled', icon: XCircle },
      converted: { label: 'Convertido', className: 'quote-detail-status-converted', icon: Package }
    }

    const config = statusConfig[status] || { label: status, className: 'quote-detail-status-default', icon: FileText }
    const Icon = config.icon

    return (
      <span className={`quote-detail-status-badge ${config.className}`}>
        <Icon size={10} />
        {config.label}
      </span>
    )
  }

  // Calcular dias até expiração
  const getDaysUntilExpiration = () => {
    if (!quote.validity_date) return null
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const validity = new Date(quote.validity_date)
    validity.setHours(0, 0, 0, 0)
    const diffTime = validity - today
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0)
  }

  const formatDate = (date) => {
    if (!date) return '-'
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }


  if (loading) {
    return (
      <div className="quote-detail-page">
        <div className="quote-detail-loading">Carregando orçamento...</div>
      </div>
    )
  }

  if (!quote) {
    return (
      <div className="quote-detail-page">
        <div className="quote-detail-error">Orçamento não encontrado</div>
      </div>
    )
  }

  const daysUntilExpiration = getDaysUntilExpiration()
  const isVencido = daysUntilExpiration !== null && daysUntilExpiration < 0 && quote.status !== 'expired' && quote.status !== 'cancelled' && quote.status !== 'converted'

  return (
    <div className="quote-detail-page">
      {/* Action Header */}
      <div className="quote-detail-action-header">
        <div className="quote-detail-action-header-left">
          <button 
            onClick={() => navigate('/erp/orcamentos')} 
            className="quote-detail-btn-back"
          >
            <ArrowLeft size={18} />
        </button>
          <div>
            <div className="quote-detail-header-title-row">
              <h1 className="quote-detail-title">#{quote.number || quote.id}</h1>
          {getStatusBadge(quote.status)}
        </div>
            <p className="quote-detail-subtitle">Sincronizado via Medik ERP</p>
      </div>
        </div>

        <div className="quote-detail-action-header-right">
          <button
            onClick={() => window.print()} 
            className="quote-detail-btn-icon"
            title="Imprimir"
          >
            <Printer size={18} />
          </button>
          <button
            onClick={handleGeneratePDF}
            disabled={generatingPDF}
            className="quote-detail-btn-icon"
            title="Baixar PDF"
          >
            <Download size={18} />
          </button>
          <div className="quote-detail-divider" />
          {hasPermission(MODULES.QUOTES, ACTIONS.CREATE) && (
          <button
              onClick={() => navigate(`/erp/orcamentos/novo?duplicar=${id}`)}
              className="quote-detail-btn-secondary"
              title="Criar novo orçamento com os mesmos itens"
          >
              <Copy size={14} /> Duplicar
          </button>
          )}
          {quote.status === 'draft' && hasPermission(MODULES.QUOTES, ACTIONS.UPDATE) && (
          <button
              onClick={() => navigate(`/erp/orcamentos/${id}/editar`)}
              className="quote-detail-btn-secondary"
          >
              <Edit3 size={14} /> Editar
          </button>
        )}
          {(quote.status === 'draft' || quote.status === 'open') && hasPermission(MODULES.QUOTES, ACTIONS.QUOTES_APPROVE) && (
          <button
              onClick={handleApprove}
              className="quote-detail-btn-primary"
          >
              <CheckCircle2 size={14} /> Aprovar Orçamento
          </button>
        )}
          {quote.status === 'approved' && hasPermission(MODULES.QUOTES, ACTIONS.QUOTES_REVERT_TO_DRAFT) && (
          <button
              onClick={handleRevertToDraft}
              className="quote-detail-btn-secondary"
          >
              <RotateCcw size={14} /> Voltar para Rascunho
          </button>
        )}
          {quote.status === 'approved'
            && hasPermission(MODULES.QUOTES, ACTIONS.QUOTES_CONVERT)
            && hasPermission(MODULES.SALES, ACTIONS.SALES_PRESALE) && (
          <button
              onClick={() => navigate(`/erp/vendas/pre-venda?quoteId=${id}`)}
              className="quote-detail-btn-primary"
          >
              <ShoppingCart size={14} /> Gerar pré-venda
          </button>
        )}
        </div>
      </div>

      <div className="quote-detail-grid">
        {/* Main Content Area: The Paper Effect */}
        <div className="quote-detail-main-content">
          <div className="quote-detail-paper">
            {/* Branding Header */}
            <div className="quote-detail-branding-header">
              <div>
                <div className="quote-detail-logo">
                  <div className="quote-detail-logo-icon">M</div>
                  <span className="quote-detail-logo-text">Medik Solutions</span>
                </div>
                <h2 className="quote-detail-paper-title">Cotação B2B</h2>
                <p className="quote-detail-paper-subtitle">Portal Corporativo de Saúde</p>
              </div>
              <div className="quote-detail-header-info">
                <div>
                  <p className="quote-detail-header-label">Emissão</p>
                  <p className="quote-detail-header-value">{formatDate(quote.quote_date || quote.created_at)}</p>
                </div>
                <div>
                  <p className="quote-detail-header-label">ID Único</p>
                  <p className="quote-detail-header-value-accent">{quote.id || quote.number}</p>
                </div>
              </div>
            </div>

            <div className="quote-detail-paper-body">
              {/* Entity Information */}
              <div className="quote-detail-entity-info">
                <div className="quote-detail-entity-section">
                  <p className="quote-detail-entity-label">Faturamento Para</p>
                  <div className="quote-detail-entity-content">
                    <p className="quote-detail-entity-name">{customer?.name || quote.customer_name || 'Cliente não informado'}</p>
                    <p className="quote-detail-entity-doc">
                      CNPJ: {customer?.cpf_cnpj || quote.customer_cpf_cnpj || 'Não informado'}
                    </p>
                  </div>
          </div>
                <div className="quote-detail-entity-section quote-detail-entity-section-right">
                  <p className="quote-detail-entity-label">Logística e Condição</p>
                  <div className="quote-detail-entity-content">
                    <p className="quote-detail-entity-payment">{quote.payment_condition || 'Não informado'}</p>
                    {daysUntilExpiration !== null && daysUntilExpiration >= 0 && (
                      <p className="quote-detail-entity-delivery">Entrega Expressa</p>
                    )}
                  </div>
          </div>
        </div>

              {/* Items List */}
              <div className="quote-detail-items-section">
                <table className="quote-detail-items-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th className="quote-detail-table-center">Qtd</th>
                      <th className="quote-detail-table-right">Unitário</th>
                      <th className="quote-detail-table-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="quote-detail-empty-items">
                          Nenhum item encontrado
                        </td>
                      </tr>
                    ) : (
                      items.map((item, index) => (
                        <tr key={item.id || index} className="quote-detail-item-row">
                          <td>
                            <p className="quote-detail-item-name">{item.product_name || 'Produto não informado'}</p>
                            {item.sku && (
                              <p className="quote-detail-item-sku">Ref: {item.sku}</p>
                            )}
                          </td>
                          <td className="quote-detail-table-center">{parseFloat(item.quantity || 0).toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>
                          <td className="quote-detail-table-right">{formatCurrency(item.unit_price)}</td>
                          <td className="quote-detail-table-right">{formatCurrency(item.total_price)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
          </div>
        </div>

            {/* Footer / Totals Section */}
            <div className="quote-detail-paper-footer">
              <div className="quote-detail-observations">
                {quote.customer_observation && (
                  <>
                    <p className="quote-detail-observations-label">Observações do Orçamento</p>
                    <p className="quote-detail-observations-text">{quote.customer_observation}</p>
                  </>
                )}
              </div>
              <div className="quote-detail-totals">
                <div className="quote-detail-total-row">
                  <span>Subtotal</span>
                  <span>{formatCurrency(quote.total_amount)}</span>
                </div>
                {quote.discount_amount > 0 && (
                  <div className="quote-detail-total-row quote-detail-total-discount">
                    <span>Desconto</span>
                    <span>- {formatCurrency(quote.discount_amount)}</span>
                  </div>
                )}
                <div className="quote-detail-total-final">
                  <p className="quote-detail-total-final-value">
                    {formatCurrency(quote.final_amount)}
                  </p>
                </div>
              </div>
          </div>
          </div>
        </div>

        {/* Sidebar Metadata */}
        <div className="quote-detail-sidebar">
          <div className="quote-detail-sidebar-card">
            <div className="quote-detail-sidebar-decoration" />
            <h3 className="quote-detail-sidebar-title">
              <Calendar size={14} className="quote-detail-sidebar-icon" /> Timeline & Prazos
            </h3>
            
            <div className="quote-detail-sidebar-content">
              <div>
                <p className="quote-detail-sidebar-label">Válido Até</p>
                <p className="quote-detail-sidebar-value">{formatDate(quote.validity_date)}</p>
                {daysUntilExpiration !== null && daysUntilExpiration >= 0 && (
                  <span className="quote-detail-expiration-warning">
                    Expira em {daysUntilExpiration} {daysUntilExpiration === 1 ? 'dia' : 'dias'}
                  </span>
                )}
                {isVencido && (
                  <span className="quote-detail-expiration-error">Vencido</span>
                )}
              </div>
              {quote.seller_name && (
                <div className="quote-detail-seller-section">
                  <p className="quote-detail-sidebar-label">Consultor Responsável</p>
                  <div className="quote-detail-seller-card">
                    <Avatar name={quote.seller_name} size="sm" />
                    <div>
                      <p className="quote-detail-seller-name">{quote.seller_name}</p>
                      <p className="quote-detail-seller-role">Vendedor Sênior</p>
                    </div>
                  </div>
          </div>
        )}
            </div>
          </div>

          <div className="quote-detail-guarantee-card">
            <div className="quote-detail-guarantee-content">
              <div className="quote-detail-guarantee-header">
                <ShieldCheck size={20} className="quote-detail-guarantee-icon" />
                <span className="quote-detail-guarantee-title">Garantia Medik Gold</span>
              </div>
              <p className="quote-detail-guarantee-text">
                Este orçamento reserva o estoque dos itens por no máximo <strong>12 horas</strong> após o envio.
              </p>
              <button className="quote-detail-guarantee-btn">Sinalizar Prioridade</button>
            </div>
          </div>
        </div>
      </div>

      {/* Overlay de Geração de PDF */}
      {generatingPDF && (
        <div className="follow-up-pdf-overlay">
          <div className="follow-up-pdf-overlay-content">
            <div className="follow-up-pdf-icon-wrapper">
              <Cpu size={32} className="spinner" />
              <div className="follow-up-pdf-scan" />
            </div>
            <div>
              <h4 className="follow-up-pdf-title">Gerando PDF</h4>
              <p className="follow-up-pdf-subtitle">Exportando Orçamento</p>
            </div>
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div className="follow-up-pdf-log">
                <p className="follow-up-pdf-log-text">{engineLogs}</p>
              </div>
              <div className="follow-up-pdf-progress-bar">
                <div className="follow-up-pdf-progress-fill" />
              </div>
            </div>
          </div>
        </div>
      )}

      {pdfQuoteData && (
        <QuotePDFTemplate
          pdfQuoteData={pdfQuoteData}
          itemsRef={pdfItemsRef}
          summaryRef={pdfSummaryRef}
          showDiscountColumn={false}
        />
      )}

      <ConfirmModal
        isOpen={confirmModalState.isOpen}
        title={confirmModalState.title}
        message={confirmModalState.message}
        onConfirm={confirmModalState.onConfirm}
        onCancel={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })}
      />

      <Toast
        message={toast.message}
        type={toast.type}
        show={toast.show}
        onClose={hideToast}
        duration={4000}
      />
    </div>
  )
}

export default OrcamentoDetailPage

